import { Box } from "@mui/material";

import { Text } from "../components/Text";
import { type Exclusion } from "./api/useWorkerBlockedStatus";
import { ChatMessage } from "./ChatMessage";
import { ChatMessageList } from "./ChatMessageList";
import { ChatBlockedState } from "./components/ChatBlockedState";
import { ChatEmptyState } from "./components/ChatEmptyState";
import { ChatErrorState } from "./components/ChatErrorState";
import { ChatLoadingState } from "./components/ChatLoadingState";
import { type ChatMessage as ChatMessageType, type ReplyContext } from "./types";

interface ChatPanelMessageContentProps {
  messages: readonly ChatMessageType[];
  workerName: string;
  workplaceId: string;
  isLoading: boolean;
  isError: boolean;
  isBlocked: boolean;
  isAdmin: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  exclusion?: Exclusion;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onRetry: () => void;
  onLoadMore: () => void;
  onReply: (context: ReplyContext) => void;
  /** Undefined disables the per-message kebab (admin / blocked viewers). */
  onMarkAsUnread?: (messageId: number) => void;
}

export function ChatPanelMessageContent(props: ChatPanelMessageContentProps) {
  const {
    messages,
    workerName,
    workplaceId,
    isLoading,
    isError,
    isBlocked,
    isAdmin,
    hasMore,
    isLoadingMore,
    exclusion,
    messagesEndRef,
    onRetry,
    onLoadMore,
    onReply,
    onMarkAsUnread,
  } = props;

  if (isLoading) {
    return <ChatLoadingState />;
  }

  if (isError) {
    return <ChatErrorState onRetry={onRetry} />;
  }

  if (isBlocked && messages.length === 0) {
    return <ChatBlockedState exclusion={exclusion} workerName={workerName} />;
  }

  if (!isBlocked && messages.length === 0) {
    return <ChatEmptyState workerName={workerName} />;
  }

  return (
    <ChatMessageList>
      {isBlocked && <ChatBlockedState exclusion={exclusion} workerName={workerName} />}
      {hasMore && (
        <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 3 }}>
          <Text
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.primary.main,
              cursor: "pointer",
            })}
            onClick={onLoadMore}
          >
            {isLoadingMore ? "Loading..." : "Load more messages"}
          </Text>
        </Box>
      )}
      {messages.map((message) => {
        const isIncoming = message.senderId !== workplaceId;
        const interactionsDisabled = isAdmin || isBlocked;

        return (
          <ChatMessage
            key={message.messageId}
            message={message}
            isIncoming={isIncoming}
            onReply={interactionsDisabled ? undefined : onReply}
            onMarkAsUnread={interactionsDisabled ? undefined : onMarkAsUnread}
          />
        );
      })}
      {/* Invisible element at the bottom to scroll to */}
      <div ref={messagesEndRef} />
    </ChatMessageList>
  );
}
